// src/pages/Login.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../axiosInstance";

const Login = ({ setUser }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [verificationAmount, setVerificationAmount] = useState("");
  const [forgotStatus, setForgotStatus] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  // Reset password state
  const [verified, setVerified] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  // Update date/time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format date and time
  const formatDateTime = (date) => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };
    return date.toLocaleDateString('en-US', options);
  };

  // -----------------------------
  // LOGIN
  // -----------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await axiosInstance.post("/login", {
        username,
        password
      });

      const { token } = res.data;

      localStorage.setItem("jwtToken", token);
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const decodedToken = JSON.parse(atob(token.split(".")[1]));

      setUser({
        name: decodedToken.name,
        role: decodedToken.role,
        id: decodedToken.id,
        isNewUser: decodedToken.isNewUser || false
      });

      if (decodedToken.isNewUser) {
        navigate("/change_password");
        return;
      }

      const redirectTo = location.state?.from || "/dashboard";
      navigate(redirectTo);

    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // FORGOT PASSWORD — VERIFY
  // -----------------------------
  const handleForgotVerify = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotStatus("");

    try {
      const res = await axiosInstance.post("/forgot-password", {
        username,
        verificationAmount
      });

      if (res.data.verified) {
        setVerified(true);
        setForgotStatus("✅ Identity verified. Set a new password.");
      } else {
        setForgotStatus(res.data.message || "Verification failed");
      }
    } catch (err) {
      setForgotStatus(
        err.response?.data?.message || "Verification failed"
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // -----------------------------
  // FORGOT PASSWORD — RESET
  // -----------------------------
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotStatus("");

    if (newPassword.length < 6) {
      setForgotStatus("Password must be at least 6 characters.");
      setForgotLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setForgotStatus("Passwords do not match.");
      setForgotLoading(false);
      return;
    }

    try {
      await axiosInstance.post("/forgot-password", {
        username,
        newPassword
      });

      setForgotStatus("✅ Password reset successfully. Please log in.");

      // Reset state
      setVerified(false);
      setShowForgot(false);
      setVerificationAmount("");
      setNewPassword("");
      setConfirmPassword("");

    } catch (err) {
      setForgotStatus(
        err.response?.data?.message || "Password reset failed"
      );
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="logo">Apollo Academy</h1>

        {/* Current Date and Time */}
        <div style={{
          textAlign: 'center',
          fontSize: '0.9rem',
          color: '#666',
          marginBottom: '1.5rem',
          padding: '0.5rem',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px'
        }}>
          {formatDateTime(currentDateTime)}
        </div>

        {/* ---------------- LOGIN ---------------- */}
        {!showForgot && (
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && <div className="error-msg">{error}</div>}

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </button>

            <div
              className="forgot-link"
              onClick={() => {
                setShowForgot(true);
                setError("");
                setForgotStatus("");
              }}
              style={{ marginTop: "12px", cursor: "pointer" }}
            >
              Forgot password?
            </div>
          </form>
        )}

        {/* ---------------- FORGOT PASSWORD ---------------- */}
        {showForgot && !verified && (
          <form className="login-form" onSubmit={handleForgotVerify}>
            <p style={{ fontSize: "14px", marginBottom: "10px" }}>
              Teachers: Enter your <strong>last payment amount</strong><br />
              Admins: Passwords must be reset directly in the database
            </p>

            <div className="form-group">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <input
                type="number"
                step="0.01"
                placeholder="Last Payment Amount"
                value={verificationAmount}
                onChange={(e) => setVerificationAmount(e.target.value)}
                required
              />
            </div>

            {forgotStatus && <div className="status-msg">{forgotStatus}</div>}

            <button type="submit" className="submit-btn" disabled={forgotLoading}>
              {forgotLoading ? "Verifying..." : "Verify"}
            </button>

            <div
              className="forgot-link"
              onClick={() => setShowForgot(false)}
              style={{ marginTop: "12px", cursor: "pointer" }}
            >
              Back to login
            </div>
          </form>
        )}

        {/* ---------------- RESET PASSWORD ---------------- */}
        {showForgot && verified && (
          <form className="login-form" onSubmit={handlePasswordReset}>
            <div className="form-group">
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {forgotStatus && <div className="status-msg">{forgotStatus}</div>}

            <button type="submit" className="submit-btn" disabled={forgotLoading}>
              {forgotLoading ? "Saving..." : "Set New Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;